import React, { useEffect, useState, createContext, useContext, Component } from 'react';
import { callWFAPI } from '../api';
export type ShapeType = 'Circle' | 'Square' | 'Oval' | 'Die Cut' | 'Rounded Corners' | 'Rectangle';
export type UploadState = 'empty' | 'loading' | 'loaded';
export interface UploadedFile {
  name: string;
  size: string;
  thumbnail?: string;
  file?: File;
}
export const SHAPE_IMAGES: Record<ShapeType, string> = {
  Circle: 'https://cdn.magicpatterns.com/uploads/eco2nrXw4X4xgfeGReJA1X/circle.png',
  Square: 'https://cdn.magicpatterns.com/uploads/cZrJBG7RNYAL8YsLBv6KcT/square.png',
  Oval: 'https://cdn.magicpatterns.com/uploads/iPVmNJFKmhCw7awqkC8UHL/oval.png',
  'Die Cut': 'https://cdn.magicpatterns.com/uploads/14na2zsNmUp928yAZPbD57/die-cut.png',
  'Rounded Corners': 'https://cdn.magicpatterns.com/uploads/2DZivzu9TaHSKqwnvKbuAc/rounded-corners.png',
  Rectangle: 'https://cdn.magicpatterns.com/uploads/1JaiP4Qmjmchyue1SdS5c7/rectangle.png'
};
export function getPriceRate(quantity: number): number {
  if (quantity <= 250) return 0.04;
  if (quantity <= 500) return 0.035;
  if (quantity <= 750) return 0.025;
  if (quantity <= 1000) return 0.02;
  return 0.018;
}
export function getTierLabel(quantity: number): string {
  if (quantity <= 250) return '1–250';
  if (quantity <= 500) return '251–500';
  if (quantity <= 750) return '501–750';
  if (quantity <= 1000) return '751–1,000';
  return '1,001+';
}
interface StickerContextType {
  // Loading
  sessionLoading: boolean;
  // Edit cart product mode
  editCartProductMode: boolean;
  // Configuration
  quantity: number;
  setQuantity: (q: number) => void;
  width: number;
  setWidth: (w: number) => void;
  height: number;
  setHeight: (h: number) => void;
  shape: ShapeType;
  setShape: (s: ShapeType) => void;
  cutIndividually: boolean;
  setCutIndividually: (c: boolean) => void;
  // Upload
  uploadedFile: UploadedFile | null;
  setUploadedFile: (f: UploadedFile | null) => void;
  uploadState: UploadState;
  setUploadState: (s: UploadState) => void;
  // Session
  sessionKey: string;
  sessionData: any;
  // Server image path
  imagePath: string;
  setImagePath: (p: string) => void;
  // Pricing (computed)
  squareInches: number;
  rate: number;
  tierLabel: string;
  baseStickerPrice: number;
  cutFee: number;
  totalPrice: number;
  pricePerSticker: number;
}
const StickerContext = createContext<StickerContextType | undefined>(undefined);
export function StickerProvider({
  children
}: {
  children: ReactNode;
}) {
  const [sessionLoading, setSessionLoading] = useState(true);
  const [editCartProductMode, setEditCartProductMode] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [shape, setShape] = useState<ShapeType>('Die Cut');
  const [cutIndividually, setCutIndividually] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('empty');
  // Session state
  const [sessionKey, setSessionKey] = useState('');
  const [sessionData, setSessionData] = useState<any>(null);
  // Server-side image path
  const [imagePath, setImagePath] = useState('');
  // Load session on mount
  useEffect(() => {
    const loadSession = async () => {
      try {
        const session = await callWFAPI(0, 'loadSessionData', {
          cookie: 1
        });
        const data = session.data;
        const key = session.key;
        setSessionData(data);
        setSessionKey(key);
        // Apply saved configuration if session data exists and is not empty
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          if (data.shape) setShape(data.shape);
          if (typeof data.quantity === 'number' && data.quantity > 0) setQuantity(data.quantity);
          if (typeof data.width === 'number' && data.width > 0) setWidth(data.width);
          if (typeof data.height === 'number' && data.height > 0) setHeight(data.height);
          if (typeof data.cutIndividually === 'boolean') setCutIndividually(data.cutIndividually);
          // Restore uploaded image from server
          if (data.imagePath && key) {
            const basename = data.imagePath.split('/').pop() || '';
            const imageUrl = 'https://www.custom-transfers.com/core/tmp/sticker-builder/' + key + '/' + encodeURIComponent(basename);
            setImagePath(data.imagePath);
            setUploadedFile({
              name: basename,
              size: '',
              thumbnail: imageUrl
            });
            setUploadState('loaded');
          }
        }
      } catch {
        // Silently ignore errors
      }
      // Check if editing an existing cart item
      try {
        if (typeof parent !== 'undefined' && parent.location && parent.location.search.match(/cart_item=\d+/)) {
          const cartItemMatch = parent.location.search.match(/cart_item=(\d+)/);
          const cartItemIndex = cartItemMatch ? parseInt(cartItemMatch[1], 10) : 0;
          const cart = await callWFAPI(30479, 'getCart', {
            cookie: 1
          });
          if (cart?.Products && cartItemIndex > 0 && cart.Products[cartItemIndex - 1]) {
            const product = cart.Products[cartItemIndex - 1];
            const options = product.Options || [];
            const getOpt = (name: string) => options.find((o: any) => o.Name === name);
            const shapeOpt = getOpt('Shape');
            if (shapeOpt?.Value) setShape(shapeOpt.Value as ShapeType);
            const widthOpt = getOpt('Width');
            if (widthOpt?.Value) setWidth(parseFloat(widthOpt.Value) || 0);
            const heightOpt = getOpt('Height');
            if (heightOpt?.Value) setHeight(parseFloat(heightOpt.Value) || 0);
            const qtyOpt = getOpt('Quantity');
            if (qtyOpt?.Value) setQuantity(parseInt(qtyOpt.Value, 10) || 0);
            const cutOpt = getOpt('Cut Option');
            if (cutOpt?.Value) setCutIndividually(cutOpt.Value === 'Yes');
            const fileOpt = getOpt('File');
            if (fileOpt?.File) {
              const filePath = fileOpt.File;
              const fileUrl = 'https://www.custom-transfers.com/' + filePath;
              const fileName = filePath.split('/').pop() || 'cart-image';
              setImagePath(filePath);
              setUploadedFile({
                name: fileName,
                size: '',
                thumbnail: fileUrl
              });
              setUploadState('loaded');
            }
            setEditCartProductMode(true);
          }
        }
      } catch {
        // Silently ignore errors
      }
      setSessionLoading(false);
    };
    loadSession();
  }, []);
  // Computed pricing
  const squareInches = width * height;
  const rate = getPriceRate(quantity);
  const tierLabel = getTierLabel(quantity);
  const baseStickerPrice = quantity * squareInches * rate;
  const cutFee = cutIndividually ? 15 : 0;
  const totalPrice = baseStickerPrice + cutFee;
  const pricePerSticker = quantity > 0 ? totalPrice / quantity : 0;
  return <StickerContext.Provider value={{
    sessionLoading,
    editCartProductMode,
    quantity,
    setQuantity,
    width,
    setWidth,
    height,
    setHeight,
    shape,
    setShape,
    cutIndividually,
    setCutIndividually,
    uploadedFile,
    setUploadedFile,
    uploadState,
    setUploadState,
    sessionKey,
    sessionData,
    imagePath,
    setImagePath,
    squareInches,
    rate,
    tierLabel,
    baseStickerPrice,
    cutFee,
    totalPrice,
    pricePerSticker
  }} data-id="element-465">
      {children}
    </StickerContext.Provider>;
}
export function useSticker() {
  const context = useContext(StickerContext);
  if (context === undefined) {
    throw new Error('useSticker must be used within a StickerProvider');
  }
  return context;
}